import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Controller from '@ember/controller';
import window from 'ember-window-mock';
import type AuthenticatorService from 'codecrafters-frontend/services/authenticator';
import type CourseModel from 'codecrafters-frontend/models/course';
import type { ModelType } from 'codecrafters-frontend/routes/catalog';
import type FeatureSuggestionModel from 'codecrafters-frontend/models/feature-suggestion';
import type LocalStorageService from 'codecrafters-frontend/services/local-storage';
import fieldComparator from 'codecrafters-frontend/utils/field-comparator';
import uniqReducer from 'codecrafters-frontend/utils/uniq-reducer';

function dismissedAudioSurveyBannerStorageKey(userId: string) {
  return `user-audio-survey-banner-dismissed:${userId}`;
}

export default class CatalogController extends Controller {
  declare model: ModelType;

  @service declare authenticator: AuthenticatorService;
  @service declare localStorage: LocalStorageService;

  @tracked audioSurveyBannerDismissedForUserId: string | null = null;
  @tracked isCreatingAudioSurveyInvite = false;

  get courses() {
    return this.model.courses.filter((course) => this.shouldDisplayCourse(course));
  }

  get languages() {
    return this.model.courses.flatMap((course) => (this.shouldDisplayCourse(course) ? course.betaOrLiveLanguages : [])).reduce(uniqReducer(), []);
  }

  get orderedCourses() {
    if (!this.authenticator.currentUser) {
      return this.courses.toSorted((course1, course2) => {
        return course1.sortPositionForTrack > course2.sortPositionForTrack ? 1 : -1;
      });
    } else {
      return this.courses.toSorted((course1, course2) => {
        const repositoriesForCourse1 = this.authenticator
          .currentUser!.repositories.filter((item) => item.course === course1)
          .filter((item) => item.lastActivityAt);
        const repositoriesForCourse2 = this.authenticator
          .currentUser!.repositories.filter((item) => item.course === course2)
          .filter((item) => item.lastActivityAt);

        const lastActivityForCourse1At =
          repositoriesForCourse1.length > 0
            ? repositoriesForCourse1.toSorted(fieldComparator('lastActivityAt')).at(-1)!.lastActivityAt.getTime()
            : null;

        const lastActivityForCourse2At =
          repositoriesForCourse2.length > 0
            ? repositoriesForCourse2.toSorted(fieldComparator('lastActivityAt')).at(-1)!.lastActivityAt.getTime()
            : null;

        if (lastActivityForCourse1At && lastActivityForCourse2At && lastActivityForCourse1At > lastActivityForCourse2At) {
          return -1;
        } else if (lastActivityForCourse1At && lastActivityForCourse2At && lastActivityForCourse1At < lastActivityForCourse2At) {
          return 1;
        } else if (lastActivityForCourse1At && !lastActivityForCourse2At) {
          return -1;
        } else if (!lastActivityForCourse1At && lastActivityForCourse2At) {
          return 1;
        } else {
          return course1.sortPositionForTrack > course2.sortPositionForTrack ? 1 : -1;
        }
      });
    }
  }

  get orderedLanguages() {
    if (!this.authenticator.currentUser) {
      return this.languages.toSorted(fieldComparator('sortPositionForTrack'));
    } else {
      return this.languages.toSorted((language1, language2) => {
        const repositoriesForLanguage1 = this.authenticator
          .currentUser!.repositories.filter((item) => item.language === language1)
          .filter((item) => item.firstSubmissionCreated);

        const repositoriesForLanguage2 = this.authenticator
          .currentUser!.repositories.filter((item) => item.language === language2)
          .filter((item) => item.firstSubmissionCreated);

        // First priority: languages with repositories come before those without
        const language1HasRepository = repositoriesForLanguage1.length > 0;
        const language2HasRepository = repositoriesForLanguage2.length > 0;

        if (language1HasRepository !== language2HasRepository) {
          return language1HasRepository ? -1 : 1;
        }

        // Second priority: among languages with repositories, sort by most recent submission first
        if (language1HasRepository && language2HasRepository) {
          // @ts-expect-error at(-1) is not defined on Array
          const lastSubmissionForLanguage1 = repositoriesForLanguage1.toSorted(fieldComparator('lastSubmissionAt')).at(-1).lastSubmissionAt;
          // @ts-expect-error at(-1) is not defined on Array
          const lastSubmissionForLanguage2 = repositoriesForLanguage2.toSorted(fieldComparator('lastSubmissionAt')).at(-1).lastSubmissionAt;

          const language1SubmissionTime = lastSubmissionForLanguage1?.getTime() || 0;
          const language2SubmissionTime = lastSubmissionForLanguage2?.getTime() || 0;

          return language2SubmissionTime - language1SubmissionTime;
        }

        // Final priority: default track order for languages without repositories
        return language1.sortPositionForTrack > language2.sortPositionForTrack ? 1 : -1;
      });
    }
  }

  get productWalkthroughFeatureSuggestion(): FeatureSuggestionModel | null {
    if (!this.authenticator.currentUser) {
      return null;
    }

    return this.authenticator.currentUser.featureSuggestions
      .filter((item) => item.featureIsProductWalkthrough)
      .filter((item) => !item.isDismissed)[0] as FeatureSuggestionModel | null;
  }

  get shouldShowUserAudioSurveyBanner() {
    const user = this.authenticator.currentUser;

    if (!user?.showUserAudioSurveyBanner) {
      return false;
    }

    if (this.audioSurveyBannerDismissedForUserId === user.id) {
      return false;
    }

    return this.localStorage.getItem(dismissedAudioSurveyBannerStorageKey(user.id)) !== '1';
  }

  @action
  async createAudioSurveyInvite() {
    if (this.isCreatingAudioSurveyInvite) {
      return;
    }

    this.isCreatingAudioSurveyInvite = true;

    // Open during the click so the browser does not block the tab after the request.
    const surveyTab = window.open('', '_blank');

    try {
      const response = await this.authenticator.currentUser!.createAudioSurveyInvite({});

      if (response?.url && surveyTab && !surveyTab.closed) {
        surveyTab.location.href = response.url;
        surveyTab.opener = null;
      } else if (response?.url && !surveyTab) {
        window.location.assign(response.url);
      } else {
        surveyTab?.close();
      }
    } catch (error) {
      surveyTab?.close();

      throw error;
    } finally {
      this.isCreatingAudioSurveyInvite = false;
    }
  }

  @action
  dismissAudioSurveyBanner() {
    const user = this.authenticator.currentUser;

    if (!user) {
      return;
    }

    this.localStorage.setItem(dismissedAudioSurveyBannerStorageKey(user.id), '1');
    this.audioSurveyBannerDismissedForUserId = user.id;
  }

  shouldDisplayCourse(course: CourseModel) {
    const userIsStaffOrCourseAuthor =
      this.authenticator.currentUser && (this.authenticator.currentUser.isStaff || this.authenticator.currentUser.isCourseAuthor(course));
    const userHasRepository =
      this.authenticator.currentUser && this.authenticator.currentUser.repositories.filter((item) => item.course === course).length > 0;

    if (course.releaseStatusIsDeprecated || course.visibilityIsPrivate) {
      return userHasRepository;
    }

    if (course.releaseStatusIsAlpha) {
      return userIsStaffOrCourseAuthor;
    }

    return true;
  }
}
